import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import styled, { useTheme } from "styled-components/macro";
import useSWR from "swr";
import { AccordionCardWithEditFeatures } from "../../../../../../components/AccordionCard/AccordionCardwithEditFeatures";
import { useAuthContext } from "../../../../../../components/Auth";
import {
  PrimaryButtonMediumV2,
  DestructiveButtonMediumV2,
  ButtonWithConfirmDialog,
  DeleteButton,
  TertiaryButtonMediumV2,
} from "../../../../../../components/Buttons/Buttons";
import { InfoBlockSmall } from "../../../../../../components/InfoBlocks/InfoBlocks";
import { Notifications } from "../../../../../../components/Notifications/NotificationsContext";
import { SearchSelectInfiniteScroll } from "../../../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { Table } from "../../../../../../components/Table/Table";
import { endpoints } from "../../../../../../endpoints";
import { TablePlaceholder, useStoreState } from "../../../../../../util/util";
import type { DataMutate } from "../../../../../../types/types";
import type {
  EntityHierarchySummarySchema,
  EntityHierarchyResponse,
  EntityProfile,
  EntityProfileSummary,
  EntityStatus,
  EntityType,
  SubsidiaryTableRow,
} from "../../../../../../types/types.MDM";
import { ENTITY_MODIFY_PERMISSIONS } from "../../../../../../types/types.MDM";
import type { OptionType } from "../../../../../../types/types";
import { AddSubsidiaryModal } from "./AddSubsidiaryModal";
import {
  PlusIcon,
  WarningIcon,
} from "../../../../../../components/Icons/Icons";
import { Modal } from "../../../../../../components/Modal/Modal";
import { useRoutePath } from "../../../../../../util/Routing";
import { TooltipPortal } from "../../../../../../components/TooltipModal/TooltipPortal";
import ReactTooltip from "react-tooltip";
import { TemplateTabData } from "../../../../../../types/types.PIM";

const SubsidiariesSection = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const SubsidiariesHeading = styled.h4`
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  margin: 0 0 ${({ theme }) => theme.resolve("spacing_lg")};
`;

const AddButtonWrapper = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_sm")};
  svg {
    width: 16px !important;
    height: 16px !important;
  }
`;

const ActionButtonsWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const ModalWrapper = styled.div`
  padding: 50px 30px ${({ theme }) => theme.resolve("spacing_2xl")};
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.small};
`;

const WarningTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  margin-bottom: 30px;
`;

function getEntityListEndpoint(
  entityType: EntityType,
  tenantId: string
): string {
  switch (entityType) {
    case "customer":
      return endpoints.v2_tenants_id_customers(tenantId);
    case "supplier":
      return endpoints.v2_tenants_id_suppliers(tenantId);
    case "distributor":
      return endpoints.v2_tenants_id_distributors(tenantId);
  }
}

const REDIRECTABLE_ENTITY_STATUSES: ReadonlySet<EntityStatus> = new Set([
  "published",
  "staged",
]);

function isEntityRedirectable(status: EntityStatus, canEdit: boolean): boolean {
  if (canEdit) {
    return true;
  }
  return REDIRECTABLE_ENTITY_STATUSES.has(status);
}

function toSubsidiaryRows(
  entities: (EntityHierarchySummarySchema | EntityProfileSummary)[],
  entityType: EntityType
): SubsidiaryTableRow[] {
  return entities.map((e) => {
    const summary = e as EntityHierarchySummarySchema;
    return {
      id: summary.id,
      name: summary.name,
      status: summary.status,
      attributes: summary.attributes,
    };
  });
}

type CompanyHierarchyProps = {
  entity: EntityProfile;
  entityType?: EntityType;
  tenantId?: string;
  mutate?: DataMutate<EntityProfile>;
  tab: TemplateTabData;
};

export const CompanyHierarchy = ({
  entity,
  entityType = "supplier",
  tenantId,
  mutate,
  tab,
}: CompanyHierarchyProps) => {
  const { adminPath } = useRoutePath();
  const [isEditMode, setIsEditMode] = useState(false);
  const { tenant_id, storefront_id } = useStoreState();
  const effectiveTenantId = tenant_id;
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { t } = useTranslation();
  const { hasPermission } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editParent, setEditParent] = useState<OptionType<string> | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isConfirmDialogDisplayed, setIsConfirmDialogDisplayed] =
    useState(false);
  const [confirmParent, setConfirmParent] =
    useState<OptionType<string> | null>(null);

  const theme = useTheme();
  const hierarchyEndpoint = endpoints.v2_tenants_id_entity_id_hierarchy(
    storefront_id,
    entity.id
  );
  const { data: hierarchyData, mutate: mutateHierarchy } =
    useSWR<EntityHierarchyResponse | null>(hierarchyEndpoint, {
      revalidateOnFocus: false,
    });

  const parentEntity = hierarchyData?.parent ?? null;
  const subsidiaries = useMemo(
    () => hierarchyData?.subsidiaries ?? [],
    [hierarchyData?.subsidiaries]
  );

  const entityListEndpoint = effectiveTenantId
    ? getEntityListEndpoint(entityType, effectiveTenantId)
    : null;

  const subsidiaryTableData = useMemo(() => {
    const source = subsidiaries;
    return toSubsidiaryRows(source, entityType);
  }, [subsidiaries, entityType]);
  const canEdit = hasPermission(ENTITY_MODIFY_PERMISSIONS[entityType]);

  const handleDeleteSubsidiary = useCallback(
    async (id: string) => {
      if (!entity || !effectiveTenantId) return;
      setIsLoading(true);
      try {
        await axios.delete(hierarchyEndpoint!, {
          data: {
            children_ids: [id],
          },
        });
        await mutateHierarchy();
        mutate?.();
        notifySuccess(t("Subsidiary deleted successfully."));
      } catch (error) {
        notifyError(t("There was an error deleting the subsidiary."), {
          error,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      entity,
      effectiveTenantId,
      mutate,
      mutateHierarchy,
      notifyError,
      notifySuccess,
      hierarchyEndpoint,
      t,
    ]
  );

  const subsidiaryColumns = useMemo(() => {
    return [
      {
        header: t("Company name"),
        accessorKey: "name",
        id: "name",
        cell: ({ row }: { row: { original: SubsidiaryTableRow } }) =>
          isEntityRedirectable(row.original.status, canEdit) ? (
            <a
              style={{
                textDecoration: "underline",
              }}
              href={`${adminPath}/mdm/${
                entityType === "supplier"
                  ? "suppliers"
                  : entityType === "customer"
                  ? "customers"
                  : "distributors"
              }/${row.original.id}`}
            >
              {row.original.name}
            </a>
          ) : (
            <span>{row.original.name}</span>
          ),
      },
      ...(hierarchyData?.subsidiary_preview_attributes
        ?.filter((attr) => attr.name !== "Company Name")
        ?.map((attribute) => ({
          header: attribute.display_name,
          accessorKey: attribute.name,
          id: attribute.name,
          cell: ({ row }: { row: { original: SubsidiaryTableRow } }) => (
            <span>
              {row.original.attributes.find((a) => a.name === attribute.name)
                ?.value ?? "--"}
            </span>
          ),
        })) ?? []),
      ...(isEditMode
        ? [
            {
              header: "",
              accessorKey: " ",
              cell: ({
                row: { original },
              }: {
                row: { original: SubsidiaryTableRow };
              }) => (
                <ButtonWithConfirmDialog
                  useV2Buttons={true}
                  Button={DeleteButton}
                  testid={`delete-subsidiary-${original.id}`}
                  handleConfirm={() => handleDeleteSubsidiary(original.id)}
                  confirmMessage={t(
                    "Are you sure you want to delete this subsidiary?"
                  )}
                />
              ),
            },
          ]
        : []),
    ];
  }, [
    t,
    hierarchyData?.subsidiary_preview_attributes,
    isEditMode,
    canEdit,
    adminPath,
    entityType,
    handleDeleteSubsidiary,
  ]);

  const handleEdit = () => {
    setEditParent(
      parentEntity
        ? { label: parentEntity.name, value: parentEntity.id }
        : { label: "", value: "" }
    );
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditParent(null);
  };

  const handleSave = async () => {
    if (!entity || !effectiveTenantId) return;
    setIsLoading(true);
    try {
      await axios.patch(hierarchyEndpoint!, {
        parent_id: editParent?.value || null,
      });
      await mutateHierarchy();
      mutate?.();
      notifySuccess(t("Company hierarchy updated successfully."));
      setIsEditMode(false);
      setEditParent(null);
    } catch (error) {
      notifyError(
        (error as any)?.response?.data?.message ??
          t("There was an error saving the company hierarchy."),
        { error }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSubsidiaries = () => {
    mutateHierarchy();
    mutate?.();
    setShowAddModal(false);
  };

  const handleEditParent = (option: OptionType<string>) => {
    if (parentEntity?.id && !isConfirmDialogDisplayed) {
      setShowConfirmDialog(true);
      setConfirmParent(option);
    } else {
      setEditParent(option);
    }
  };

  if (!entity && !effectiveTenantId) {
    return (
      <AccordionCardWithEditFeatures
        title={t("Company hierarchy")}
        expanded={true}
      >
        <InfoBlockSmall
          header={t("Parent Entity")}
          content={
            <span style={{ color: theme.resolve("color_text_tertiary") }}>
              {t(
                "Company hierarchy is available when viewing an entity detail page."
              )}
            </span>
          }
        />
      </AccordionCardWithEditFeatures>
    );
  }

  if (!entity) {
    return null;
  }

  const isArchived = entity.status === "archived";
  const isParentRedirectable =
    parentEntity != null && isEntityRedirectable(parentEntity.status, canEdit);
  return (
    <AccordionCardWithEditFeatures
      title={t("Company hierarchy")}
      handleEdit={canEdit && !isArchived ? handleEdit : undefined}
      handleCancel={isEditMode ? handleCancel : undefined}
      expanded={true}
      editing={isEditMode}
    >
      {!isEditMode && (
        <>
          {parentEntity && (
            <TooltipPortal>
              <ReactTooltip
                id={entity.id}
                place="top"
                effect="solid"
                backgroundColor={theme.tooltipBG}
              >
                <div>
                  {hierarchyData?.parent_preview_attributes?.map(
                    (attribute) => {
                      const value =
                        hierarchyData?.parent?.attributes?.find(
                          (a) => a.name === attribute.name
                        )?.value ?? "--";

                      return (
                        <div key={attribute.name}>
                          <span style={{ opacity: 0.8 }}>
                            {attribute.display_name}:
                          </span>{" "}
                          <span>{value}</span>
                        </div>
                      );
                    }
                  )}
                </div>
              </ReactTooltip>
            </TooltipPortal>
          )}
          <div>
            <InfoBlockSmall
              header={t("Parent Entity")}
              content={
                parentEntity ? (
                  isParentRedirectable ? (
                    <a
                      data-tip
                      data-for={entity.id}
                      style={{
                        color: theme.resolve("color_text_primary"),
                        textDecoration: "underline",
                      }}
                      href={`${adminPath}/mdm/${
                        entityType === "supplier"
                          ? "suppliers"
                          : entityType === "customer"
                          ? "customers"
                          : "distributors"
                      }/${parentEntity.id}`}
                    >
                      {parentEntity.name}
                    </a>
                  ) : (
                    <span
                      data-tip
                      data-for={entity.id}
                      style={{
                        color: theme.resolve("color_text_primary"),
                      }}
                    >
                      {parentEntity.name}
                    </span>
                  )
                ) : (
                  <span style={{ color: theme.resolve("color_text_tertiary") }}>
                    --
                  </span>
                )
              }
            />
          </div>
        </>
      )}
      {isEditMode && (
        <>
          <div style={{ maxWidth: "600px" }}>
            <SearchSelectInfiniteScroll
              baseUrl={entityListEndpoint}
              getOptions={(data: EntityProfileSummary[]) =>
                data
                  .filter(
                    (e) =>
                      e.id !== entity.id &&
                      e.id !== parentEntity?.id &&
                      !subsidiaries.some((s) => s.id === e.id)
                  )
                  .map((e) => ({ label: e.name, value: e.id }))
              }
              placeholder={t("Parent Entity")}
              isClearable={true}
              name="parent"
              value={editParent ?? { label: "", value: "" }}
              onChange={handleEditParent}
              formState={{} as any}
              errors={{}}
            />
          </div>
        </>
      )}
      <SubsidiariesSection>
        <SubsidiariesHeading>{t("Subsidiaries")}</SubsidiariesHeading>
        <Table
          columns={subsidiaryColumns}
          data={subsidiaryTableData}
          isLoading={false}
          error={undefined}
          lastChildleftAlign={true}
          Placeholder={<TablePlaceholder message={t("No items to show.")} />}
        />
      </SubsidiariesSection>
      {isEditMode && (
        <>
          <AddButtonWrapper style={{ marginTop: theme.resolve("spacing_lg") }}>
            <TertiaryButtonMediumV2 onClick={() => setShowAddModal(true)}>
              <PlusIcon fill={theme.resolve("color_text_tertiary")} />
              {t("Add")}
            </TertiaryButtonMediumV2>
          </AddButtonWrapper>
          <ActionButtonsWrapper>
            <DestructiveButtonMediumV2 onClick={handleCancel}>
              {t("Cancel")}
            </DestructiveButtonMediumV2>
            <PrimaryButtonMediumV2 onClick={handleSave} loading={isLoading}>
              {t("Save")}
            </PrimaryButtonMediumV2>
          </ActionButtonsWrapper>
        </>
      )}
      <AddSubsidiaryModal
        show={showAddModal}
        entityType={entityType}
        storefrontId={storefront_id}
        hierarchyEndpoint={hierarchyEndpoint}
        currentEntityId={entity.id}
        onAdd={handleAddSubsidiaries}
        onCancel={() => setShowAddModal(false)}
      />
      <Modal
        overlay
        show={showConfirmDialog}
        closeModal={() => {
          setShowConfirmDialog(false);
          setConfirmParent(null);
        }}
        modalWidth={"600px"}
      >
        <ModalWrapper>
          <div style={{ marginBottom: "15px" }}>
            <WarningIcon width={50} height={50} />
          </div>
          <WarningTitle>
            {t(
              `Changing the parent supplier will unlink this supplier from the existing parent supplier and will break the existing mapping.`
            )}
          </WarningTitle>
          <div style={{ display: "flex", marginTop: "30px" }}>
            <TertiaryButtonMediumV2
              fullWidth
              onClick={() => {
                setShowConfirmDialog(false);
                setConfirmParent(null);
              }}
              style={{ marginRight: "10px" }}
            >
              {t("Cancel")}
            </TertiaryButtonMediumV2>
            <PrimaryButtonMediumV2
              fullWidth
              onClick={() => {
                setEditParent(confirmParent);
                setShowConfirmDialog(false);
                setIsConfirmDialogDisplayed(true);
                setConfirmParent(null);
              }}
              style={{ marginLeft: "10px" }}
            >
              {t("Confirm")}
            </PrimaryButtonMediumV2>
          </div>
        </ModalWrapper>
      </Modal>
    </AccordionCardWithEditFeatures>
  );
};
